/**
 * Rule Suggestions review-queue API client — /api/v1/rule-suggestions.
 *
 * Triage queue for corpus-mined scope-rule suggestions. Reuses the same
 * Firebase auth interceptor pattern as ruleReviewApi.ts / rulesApi.ts.
 */

import axios from 'axios';
import { auth } from './firebase';
import type {
  BulkAction,
  BulkActionResponse,
  SuggestionListResponse,
  SuggestionStats,
} from '../types/ruleSuggestions';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('[ruleSuggestionsApi] could not get firebase token', err);
    }
  } else if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  ) {
    config.headers.Authorization = 'Bearer dev-bypass-token';
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      'Rule suggestions request failed';
    return Promise.reject(new Error(message));
  },
);

export interface SuggestionListParams {
  source_corpus?: string;
  min_confidence?: number;
  rule_type?: string;
  severity?: string;
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export const RuleSuggestionsAPI = {
  list: (params?: SuggestionListParams): Promise<SuggestionListResponse> =>
    client.get('/rule-suggestions', { params }),

  stats: (): Promise<SuggestionStats> =>
    client.get('/rule-suggestions/stats'),

  bulk: (body: BulkAction): Promise<BulkActionResponse> =>
    client.post('/rule-suggestions/bulk', body),
};
